import { useEffect, useState } from 'react';
import { supabase } from '../supabase';

/**
 * Creatività dell'annuncio Meta da cui arriva un lead: immagine, titolo, testo,
 * invito all'azione, link di anteprima.
 *
 * Due tempi, per non far aspettare chi apre la scheda:
 *  1. lettura immediata dalla memoria in `ad_ads` (l'immagine c'è per tutti gli
 *     annunci, quindi il riquadro compare subito);
 *  2. se manca il testo, chiamata alla funzione `meta-ad-creative`, che lo chiede
 *     a Meta una volta sola e lo salva in memoria per le volte successive.
 *
 * Perché serve: il testo dell'annuncio era assente per due terzi delle creatività
 * (122 su 361 verificate in produzione il 06/09/2026), perché la sincronizzazione
 * chiedeva a Meta un insieme di campi troppo ridotto.
 *
 * Lo stesso dato serve in due posti (riga Origine del pannello e blocco "lead
 * arrivato" della cronologia): sta qui perché non vengano scritte due volte né le
 * regole di lettura né la logica di richiamo.
 */

export interface AdCreative {
  headline: string | null;
  body: string | null;
  description: string | null;
  imageUrl: string | null;
  callToAction: string | null;
  linkUrl: string | null;
  previewLink: string | null;
  creativeType: string | null;
}

export interface UseAdCreativeResult {
  creative: AdCreative | null;
  /** Prima lettura dalla memoria in corso: si mostra lo scheletro. */
  loading: boolean;
  /** Testo in arrivo da Meta: si mostra "Recupero il testo…". */
  enriching: boolean;
  /** `meta_non_collegato`, `meta_errore`, `non_meta`, `nessun_annuncio`, `errore`. */
  reason: string | null;
}

/**
 * Invito all'azione, link di anteprima e destinazione: non hanno una colonna
 * dedicata in `ad_ads`, vivono dentro la risposta grezza di Meta che la funzione
 * salva in `platform_data`. Si leggono qui per mostrarli anche quando la
 * creatività arriva dalla memoria, senza richiamare Meta.
 */
function extraDaPlatformData(raw: any): Pick<AdCreative, 'callToAction' | 'linkUrl' | 'previewLink'> {
  const c = raw?.creative ?? {};
  const link = c.object_story_spec?.link_data ?? {};
  const video = c.object_story_spec?.video_data ?? {};
  const feed = c.asset_feed_spec ?? {};
  return {
    callToAction:
      c.call_to_action_type ||
      link.call_to_action?.type ||
      video.call_to_action?.type ||
      (Array.isArray(feed.call_to_action_types) ? feed.call_to_action_types[0] : null) ||
      null,
    linkUrl: link.link || feed.link_urls?.[0]?.website_url || null,
    previewLink: raw?.preview_shareable_link || null,
  };
}

/**
 * Vero se l'indirizzo è la miniatura 64x64 di Meta e non la creatività vera:
 * `thumbnail_url` torna sempre firmata a quella dimensione (`stp=...p64x64...`).
 */
export function isTinyThumb(url: string | null | undefined): boolean {
  return !!url && url.includes('p64x64');
}

/**
 * Una sola richiesta per annuncio anche con due card sulla stessa scheda (riga
 * Origine e blocco in cronologia, per di più montate due volte dai layout
 * desktop e mobile): le promesse in volo e i risultati stanno in una mappa a
 * livello di modulo, chiavata su organizzazione e annuncio.
 */
type Esito = { creative: AdCreative | null; reason: string | null };
const inVolo = new Map<string, Promise<Esito>>();
const risultati = new Map<string, Esito>();

/** L'immagine grande non esiste per questo annuncio: lo segna la funzione, inutile richiedere. */
function immagineGrandeNonDisponibile(raw: any): boolean {
  return raw?._full_image_unavailable === true;
}

async function caricaCreativita(leadId: string, adId: string, organizationId: string): Promise<Esito> {
  const { data: row } = await supabase
    .from('ad_ads')
    .select('headline, body_text, creative_url, creative_type, platform_data')
    .eq('organization_id', organizationId)
    .eq('platform', 'meta')
    .eq('external_id', adId)
    .maybeSingle();

  const extra = extraDaPlatformData(row?.platform_data);
  const daMemoria: AdCreative | null = row
    ? {
        headline: row.headline?.trim() || null,
        body: row.body_text?.trim() || null,
        description: null,
        imageUrl: row.creative_url || null,
        callToAction: extra.callToAction,
        linkUrl: extra.linkUrl,
        previewLink: extra.previewLink,
        creativeType: row.creative_type || null,
      }
    : null;

  const mancaTesto = !daMemoria?.headline && !daMemoria?.body;
  const mancaImmagine = isTinyThumb(daMemoria?.imageUrl) && !immagineGrandeNonDisponibile(row?.platform_data);
  if (!mancaTesto && !mancaImmagine) return { creative: daMemoria, reason: null };

  try {
    const { data, error } = await supabase.functions.invoke('meta-ad-creative', { body: { lead_id: leadId } });
    if (error) return { creative: daMemoria, reason: 'errore' };
    return { creative: (data?.creative as AdCreative) ?? daMemoria, reason: data?.reason ?? null };
  } catch {
    return { creative: daMemoria, reason: 'errore' };
  }
}

export function useAdCreative(leadId: string, adId: string, organizationId: string): UseAdCreativeResult {
  const [creative, setCreative] = useState<AdCreative | null>(null);
  const [loading, setLoading] = useState(true);
  const [enriching, setEnriching] = useState(false);
  const [reason, setReason] = useState<string | null>(null);

  useEffect(() => {
    let annullato = false;
    const chiave = `${organizationId}:${adId}`;
    setCreative(null);
    setReason(null);

    const pronto = risultati.get(chiave);
    if (pronto) {
      setCreative(pronto.creative);
      setReason(pronto.reason);
      setLoading(false);
      return;
    }

    setLoading(true);
    setEnriching(true);
    let promessa = inVolo.get(chiave);
    if (!promessa) {
      promessa = caricaCreativita(leadId, adId, organizationId).finally(() => inVolo.delete(chiave));
      inVolo.set(chiave, promessa);
    }
    promessa.then((esito) => {
      risultati.set(chiave, esito);
      if (annullato) return;
      setCreative(esito.creative);
      setReason(esito.reason);
      setLoading(false);
      setEnriching(false);
    });
    return () => { annullato = true; };
  }, [leadId, adId, organizationId]);

  return { creative, loading, enriching, reason };
}

/** Etichette italiane degli inviti all'azione di Meta. */
export const CTA_LABELS: Record<string, string> = {
  APPLY_NOW: 'Candidati ora',
  LEARN_MORE: 'Scopri di più',
  SIGN_UP: 'Iscriviti',
  BOOK_TRAVEL: 'Prenota',
  CONTACT_US: 'Contattaci',
  DOWNLOAD: 'Scarica',
  GET_QUOTE: 'Richiedi preventivo',
  GET_OFFER: 'Ottieni offerta',
  MESSAGE_PAGE: 'Invia messaggio',
  SUBSCRIBE: 'Abbonati',
  SHOP_NOW: 'Acquista ora',
  WHATSAPP_MESSAGE: 'Scrivi su WhatsApp',
};

/** Etichette italiane del tipo di creatività. */
export const TYPE_LABELS: Record<string, string> = {
  video: 'Video',
  carousel: 'Carosello',
  dynamic: 'Dinamico',
  image: 'Immagine',
};
